'use es6';

import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import _ from 'underscore';
import { toSnakeCase } from 'ContentEditorUI/utils/helpers';
import staticErrorKeys from 'ContentEditorUI/constants/StaticErrorKeys';
import { domainIsStaging } from 'ContentEditorUI/utils/domainsUtils';
import cannotPublishOnDomainReasons from '../constants/cannotPublishOnDomainReasons';
import { captureMessage } from 'ContentEditorUI/lib/exceptions';
import domainExclusionReasons from 'ContentEditorUI/constants/domainExclusionReasons';
import { jsx as _jsx } from "react/jsx-runtime";
export const getNiceFieldName = (fieldName, error) => {
  let name = '';
  if (error.fieldSchema && error.fieldSchema.title) {
    name = I18n.SafeString(error.fieldSchema.title);
  } else {
    const defaultValue = fieldName;
    name = I18n.text(`components.header.validationErrorPopover.reasonItems.${toSnakeCase(fieldName)}`, {
      defaultValue
    });
  }
  return name;
};
const getKeyFromString = str => {
  return document.querySelector(str).dataset.key;
};
const defaultLabelHandlers = {
  required: (error, fieldName) => ({
    message: 'editor.errors.required',
    data: {
      niceFieldName: getNiceFieldName(fieldName, error)
    },
    target: fieldName,
    pane: error.pane,
    useHtml: true,
    onClickData: {
      settingsModalQuery: error.settingsModalQuery
    }
  }),
  moduleDataValidation: error => {
    const key = `app.errors.moduleDataValidation.${error.errorType}`;
    return {
      message: I18n.lookup(key) ? key : 'app.errors.moduleDataValidation.default',
      pane: error.pane,
      data: error.errorTokens,
      onClickData: {
        moduleId: error.errorTokens.id
      },
      target: 'content'
    };
  },
  temporarySlugNotAllowed: (error, fieldName) => ({
    message: 'editor.errors.temporarySlugNotAllowed',
    target: fieldName,
    pane: error.pane,
    useHtml: true,
    onClickData: {
      settingsModalQuery: error.settingsModalQuery
    }
  }),
  advancedModuleWithoutAccess: error => {
    return {
      message: 'editor.errors.removeModuleError',
      data: {
        number: error.fieldSchema.amount,
        fieldName: error.fieldSchema.title,
        moduleType: error.fieldSchema.moduleType
      },
      pane: error.pane,
      onClickData: {
        moduleId: error.moduleId
      },
      target: 'contentPane',
      useHtml: true
    };
  },
  paymentFieldWithoutAccess: error => {
    return {
      message: `editor.errors.paymentsFieldWithoutAccess.${error.fieldSchema.amount > 1 ? 'multiple' : 'single'}`,
      data: {
        number: error.fieldSchema.amount
      },
      pane: error.pane,
      onClickData: {
        moduleId: error.moduleId
      },
      target: 'contentPane',
      useHtml: true
    };
  },
  personalizationTokenWithoutAccess: error => {
    return {
      message: `editor.errors.personalizationTokenWithoutAccess.${error.fieldSchema.amount > 1 ? 'multiple' : 'single'}`,
      data: {
        number: error.fieldSchema.amount
      },
      pane: error.pane,
      onClickData: {
        moduleId: error.moduleId
      },
      target: 'contentPane',
      useHtml: true
    };
  }
};
export const formatErrorMessage = errorLabel => {
  const {
    target,
    onClickData,
    message,
    data,
    useHtml
  } = errorLabel;
  const Translation = useHtml ? FormattedHTMLMessage : FormattedReactMessage;
  return {
    target,
    onClickData,
    node: /*#__PURE__*/_jsx(Translation, {
      message: message,
      options: data
    })
  };
};
export const getErrorLabels = (errors, errorHandlers = {}, state = {}) => {
  const handlers = Object.assign({}, defaultLabelHandlers, errorHandlers);
  // Only read attacker-reachable fields as own properties so a polluted
  // Object.prototype (e.g. via the prototype pollution gadget described in
  // https://github.com/HubSpotEngineering/pages/issues/5278) cannot inject a
  // messageKey that gets rendered as raw HTML.
  const hasOwn = (obj, prop) => _.isObject(obj) && Object.prototype.hasOwnProperty.call(obj, prop);
  return _.flatten(_.pairs(errors).reduce((labels, [key, error]) => {
    const type = hasOwn(error, 'type') ? error.type : key;
    if (_.isObject(error)) {
      const onClickData = hasOwn(error, 'settingsModalQuery') ? {
        settingsModalQuery: error.settingsModalQuery
      } : undefined;
      const ownMessageKey = hasOwn(error, 'messageKey') ? error.messageKey : undefined;
      if (ownMessageKey || staticErrorKeys[type]) {
        labels.push({
          message: ownMessageKey || staticErrorKeys[type],
          target: hasOwn(error, 'target') ? error.target : 'content',
          useHtml: true,
          data: hasOwn(error, 'options') ? error.options : undefined,
          pane: hasOwn(error, 'pane') ? error.pane : undefined,
          onClickData
        });
      } else if (hasOwn(handlers, type)) {
        labels.push(handlers[type](error, key, state));
      } else if (hasOwn(error, 'message') || hasOwn(error, 'customComponent')) {
        labels.push(error);
      }
      return labels;
    }
    const message = getKeyFromString(error);
    if (message) {
      labels.push({
        message: getKeyFromString(error)
      });
    } else {
      console.log(`No label handler for validation error ${error.type}`, error);
      captureMessage('No label handler for validation error', {
        level: 'error',
        extra: {
          error
        }
      });
    }
    return labels;
  }, []));
};
export const getCannotPublishOnDomainReason = ({
  domain,
  domainIsNotPermitted,
  domainExclusionReason,
  contentTypeStr,
  userHasPublishAccess,
  isBlog = false
}) => {
  if (domainIsNotPermitted) {
    if (domainExclusionReason === domainExclusionReasons.MISSING_PERMISSION) {
      return cannotPublishOnDomainReasons.NO_DOMAIN_PERMISSION;
    }
    if (domainExclusionReason === domainExclusionReasons.ACTIVE_DOWNGRADE) {
      return isBlog ? cannotPublishOnDomainReasons.ACTIVE_DOWNGRADE_BLOG : cannotPublishOnDomainReasons.ACTIVE_DOWNGRADE;
    }
    if (domainExclusionReason === domainExclusionReasons.REDIRECT_DOMAIN) {
      return cannotPublishOnDomainReasons.REDIRECTING_DOMAIN;
    }
    if (domainExclusionReason === domainExclusionReasons.NOT_CONTENT_HOSTING) {
      return cannotPublishOnDomainReasons.NOT_CONTENT_HOSTING;
    }
  }
  if (!userHasPublishAccess) {
    return cannotPublishOnDomainReasons.USER_HAS_NO_PUBLISH_ACCESS;
  }
  if (!domain.isResolving) {
    return cannotPublishOnDomainReasons.NON_RESOLVING_DOMAIN;
  }
  if (domain.secondaryToDomain) {
    return cannotPublishOnDomainReasons.REDIRECTING_DOMAIN;
  }
  if (!domain.brandId && !domain.isInternalDomain) {
    return cannotPublishOnDomainReasons.DOMAIN_NOT_ASSOCIATED_WITH_BRAND;
  }
  if (!domain[`isUsedFor${contentTypeStr}`] && !domain[`primary${contentTypeStr}`] && !domain.isInternalDomain && !domainIsStaging(domain)) {
    return cannotPublishOnDomainReasons.DOMAIN_NOT_PRIMARY_OR_SECONDARY_FOR_CONTENT_TYPE;
  }
  return undefined;
};